import { glom } from '../../../Signage/glom';
import { ReachAPI } from '../../../ReachApi';
import { Signage } from '../../../Signage';
import {
    VistarWidgetConfiguration,
    VistarWidgetTimelineSlot,
    VistarAd,
    VistarWidgetConfigurationDetails,
    ZodVistarWidgetConfigurationDetails,
} from './types';
import { FadeInCommand, WidgetType } from '../../types';
import { Log } from '../../../AuxillaryService/Log';
import { VistarApiHelper } from './VistarApiHelper';
import { createVistarVideoHandler } from '../../../MediaController';
import { PlayerTokenError } from '../errors';
import { GetTokenAPISuccessResponse } from '../../../ReachApi/vistarTypes';
import { BaseThirdPartyAdWidget } from '../BaseThirdPartyAdWidget';
import AdQueue from '../AdQueue';
import { VistarVideoHandler } from '../../../MediaController/Video';

export class VistarWidget extends BaseThirdPartyAdWidget {
    type: WidgetType.VISTAR;
    declare config: VistarWidgetConfigurationDetails;

    protected readonly adQueue: AdQueue<VistarAd> = new AdQueue();

    /**
     * Embed token for the resource
     */
    #apiKey: string | null;

    /**
     * network id for Vistar API req
     */
    #networkId: string | null;

    /**
     * This is the user-defined ID of the venue that is making the request.
     * It will be used to look up the venue’s location, CPM, and impression data.
     */
    readonly #venueId: string;

    constructor(widgetConfiguration: VistarWidgetConfiguration, zoneId: number) {
        super(widgetConfiguration, zoneId);
        this.type = WidgetType.VISTAR;
        this.#apiKey = null;
        this.#networkId = null;
        this.#venueId = this.config.venue_id;
    }

    protected validateConfigurationDetails(
        vistarWidgetConfigurationDetails: VistarWidgetConfigurationDetails
    ): VistarWidgetConfigurationDetails {
        return ZodVistarWidgetConfigurationDetails.parse(vistarWidgetConfigurationDetails);
    }

    /**
     * Get embed token & network_id used for Vistar
     * api requests
     * @throws {@link PlayerTokenError} when token player token or UUID
     * isn't set
     * @throws {@link VistarAPICredentialsError } when the reach API
     * fails to retrieve credentials stored for making Vistar API requests.
     */
    private async getVistarCredentials(): Promise<void> {
        if (!glom.playerUuid && !glom.securityToken) {
            throw new PlayerTokenError('Player uuid or security token must be set');
        }
        const security = new ReachAPI.Security(
            glom.playerUuid || undefined,
            glom.securityToken || undefined
        );
        try {
            const data: GetTokenAPISuccessResponse = await ReachAPI.vistar.getCredentials(
                security,
                this.id
            );
            this.#apiKey = data.data.credentials.api_key;
            this.#networkId = data.data.credentials.network_id;
            Log.Info(`${this.name} successfully retrieved Vistar API token `, null, __fileline);
        } catch (error) {
            Log.Warning(`${this.name}: failed to retrieve credentials`, null, __fileline);
        }
    }

    /**
     * send proof of play and remove played
     * ads from dom,  will also fetch ads if the current
     * slot it the last slot place exchange slot in the current loop
     */
    async afterFadeOut(currentSlot: VistarWidgetTimelineSlot): Promise<void> {
        if (!currentSlot.params.ad.isFallback && currentSlot.params.ad.playcount >= 1) {
            if (this.#apiKey && this.#networkId) {
                await VistarApiHelper.sendProofOfPlay(currentSlot.params.ad);
            }
        }

        await super.afterFadeOut(currentSlot);
    }

    /**
     * Set current slot's ad's display time
     * to the current time in UTC epoch seconds if
     * ad is content retrieved from Vistar API.
     */
    async afterFadeIn(
        currentSlot: VistarWidgetTimelineSlot
    ): Promise<void | Partial<FadeInCommand>> {
        if (currentSlot.params.ad.isFallback === false) {
            const now = Date.now();
            const currentTimeAsEpochSeconds = Math.trunc(now / 1000);

            currentSlot.params.ad.displayTime = currentTimeAsEpochSeconds;
        }

        await super.afterFadeIn(currentSlot);
    }

    /**
     * Fetches Vistar ads.
     */
    async getAds(): Promise<void> {
        await this.getVistarCredentials();
        const displayTimeGuess = Date.now() + 15_000; // the guesstimate of when ads will be show is 15 seconds from now.
        if (!this.#apiKey || !this.#networkId) {
            Log.Error(
                `${this.name} failed to fetch ads, API key or network ID not set.`,
                null,
                __fileline
            );
            return;
        }
        const predictedDisplayTimeInEpochSeconds = Math.trunc(displayTimeGuess / 1000);

        await VistarApiHelper.fetchAds(
            this.#networkId,
            this.#apiKey,
            this.#venueId,
            this.adWidth,
            this.adHeight,
            predictedDisplayTimeInEpochSeconds,
            this.querySlotsCallNo === 1 ? this.numberOfAds * 2 : this.numberOfAds,
            Signage.playerUuid ?? String(this.id),
            this.durationInSeconds,
            this.adQueue
        );
    }

    getThirdPartyAdVendorVideoHandler(assetUrl: string): VistarVideoHandler {
        return createVistarVideoHandler({
            videoUrl: assetUrl,
            muted: this.muteAds,
        });
    }
}
